import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { registry } from '../../core/Registry.js';

const Wrapper = styled.div`
  width: ${props => props.width || '100%'};
  max-width: ${props => props.maxWidth || '100%'};
  margin: ${props => props.margin || '0 auto'};
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-sm, 12px);
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  user-select: none;
`;

const ImageLayer = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
`;

const BeforeLayerWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${props => props.sliderPos}%;
  height: 100%;
  overflow: hidden;
`;

const BeforeImage = styled.img`
  width: ${props => props.containerWidth}px;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const SliderLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${props => props.sliderPos}%;
  width: 4px;
  margin-left: -2px;
  background: #fff;
  cursor: ew-resize;
  box-shadow: 0 0 10px rgba(0,0,0,0.5);

  &::after {
    content: '↔';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
    background: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;
    font-size: 18px;
    font-weight: bold;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  }
`;

const Label = styled.div`
  position: absolute;
  bottom: 15px;
  padding: 6px 12px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  pointer-events: none;
  
  &.before-label { left: 15px; }
  &.after-label { right: 15px; }
`;

const BeforeAfterWidget = ({ node, isLiveMode }) => {
  const [sliderPos, setSliderPos] = useState(50);
  const [containerWidth, setContainerWidth] = useState(800);
  const containerRef = useRef(null);

  const imgBefore = node.content?.imgBefore || 'https://via.placeholder.com/800x400/333333/ffffff?text=Before';
  const imgAfter = node.content?.imgAfter || 'https://via.placeholder.com/800x400/00A9E0/ffffff?text=After';
  const labelBefore = node.content?.labelBefore || 'Davor';
  const labelAfter = node.content?.labelAfter || 'Danach';

  const width = node.style?.desktop?.width || '100%';

  const handleDrag = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX || (e.touches && e.touches[0].clientX);
    if (!x) return;
    
    let pos = ((x - rect.left) / rect.width) * 100;
    pos = Math.max(0, Math.min(100, pos));
    setSliderPos(pos);
  };

  const handleUpdateWidth = () => {
    if(containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  };

  return (
    <Wrapper 
      width={width} 
      ref={containerRef}
      onMouseMove={(e) => { if(e.buttons === 1) handleDrag(e); }}
      onTouchMove={handleDrag}
      onLoad={handleUpdateWidth}
    >
      <ImageLayer src={imgAfter} alt="After" />
      <BeforeLayerWrapper sliderPos={sliderPos}>
        <BeforeImage src={imgBefore} alt="Before" containerWidth={containerWidth || 800} />
      </BeforeLayerWrapper>
      
      <SliderLine sliderPos={sliderPos} />
      
      <Label className="before-label" style={{ opacity: sliderPos > 20 ? 1 : 0 }}>{labelBefore}</Label>
      <Label className="after-label" style={{ opacity: sliderPos < 80 ? 1 : 0 }}>{labelAfter}</Label>
    </Wrapper>
  );
};

registry.register('before_after', {
  label: 'Bilder-Vergleich',
  icon: '🌓',
  category: 'advanced',
  defaultContent: {
    imgBefore: '',
    imgAfter: '',
    labelBefore: 'Davor',
    labelAfter: 'Danach'
  },
  defaultStyle: {
    desktop: {
      width: '100%'
    }
  },
  render: BeforeAfterWidget,
  inspectorControls: [
    {
      tab: 'content',
      groups: [
        {
          title: 'Bilder',
          fields: [
            { name: 'content.imgBefore', label: 'Bild URL 1 (Davor)', type: 'text' },
            { name: 'content.imgAfter', label: 'Bild URL 2 (Danach)', type: 'text' }
          ]
        },
        {
          title: 'Labels',
          fields: [
            { name: 'content.labelBefore', label: 'Text 1 (Links)', type: 'text' },
            { name: 'content.labelAfter', label: 'Text 2 (Rechts)', type: 'text' }
          ]
        }
      ]
    },
    {
      tab: 'style',
      groups: [
        {
          title: 'Dimensionen',
          fields: [
            { name: 'style.desktop.width', label: 'Breite (z.B. 100%, 800px)', type: 'text' }
          ]
        }
      ]
    }
  ]
});
